
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useData } from '../context/DataContext';
import { sendFormEmail } from '../utils/emailService';

const Contact: React.FC = () => {
  const { addInquiry, settings } = useData();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    // STEP 1: Save to Database FIRST (guaranteed)
    addInquiry({
      id: Math.random().toString(36).substr(2, 9),
      ...formData,
      type: 'General',
      status: 'New',
      date: new Date().toISOString()
    });

    // STEP 2: Show success to user IMMEDIATELY
    setSubmitted(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    setIsSending(false);
    setTimeout(() => setSubmitted(false), 5000);

    // STEP 3: Try to send email notification in background (non-blocking)
    sendFormEmail(formData, 'contact');
  };

  return (
    <div className="min-h-screen bg-neutral-900 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="font-display text-5xl font-bold text-white uppercase tracking-wider">Get in Touch</h1>
          <p className="text-gray-400 mt-4">Visit our HQ in Dar es Salaam or contact our regional offices.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-neutral-800 p-8 rounded-sm border-l-4 border-automax-red">
              <h3 className="font-display text-2xl text-white uppercase mb-4">Headquarters</h3>
              <div className="flex items-start text-gray-300 mb-4">
                <MapPin className="mr-4 text-automax-red flex-shrink-0" />
                <p>{settings.address}</p>
              </div>
              <div className="flex items-center text-gray-300 mb-4">
                <Phone className="mr-4 text-automax-red flex-shrink-0" />
                <p>{settings.phoneNumber}</p>
              </div>
              <div className="flex items-center text-gray-300">
                <Mail className="mr-4 text-automax-red flex-shrink-0" />
                <div>
                  <p>{settings.email}</p>
                  {settings.secondaryEmail && <p className="text-sm text-gray-400 mt-1">{settings.secondaryEmail}</p>}
                </div>
              </div>
            </div>

            <div className="h-64 bg-gray-800 rounded-sm overflow-hidden relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1981.0012381815782!2d39.27342225423247!3d-6.769550441360269!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c4d8821e0d22f%3A0xa9673558186a9454!2sPeninsula%20Noble%20Center!5e0!3m2!1sen!2stz!4v1770836115450!5m2!1sen!2stz"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              ></iframe>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-sm shadow-xl">
            <h3 className="font-display text-2xl text-gray-900 uppercase mb-6 font-bold">Request a Quote</h3>

            {submitted ? (
              <div className="bg-green-100 border border-green-200 text-green-700 p-4 rounded-sm mb-6 text-center">
                <strong>Thank you!</strong> Your message has been sent. Our team will contact you shortly.
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Full Name</label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full bg-gray-50 border border-gray-300 p-3 rounded-sm focus:border-automax-red outline-none text-gray-900 placeholder-gray-500" placeholder="Your Name" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Email Address</label>
                  <input required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full bg-gray-50 border border-gray-300 p-3 rounded-sm focus:border-automax-red outline-none text-gray-900 placeholder-gray-500" placeholder="email@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
                  <input required type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-gray-50 border border-gray-300 p-3 rounded-sm focus:border-automax-red outline-none text-gray-900 placeholder-gray-500" placeholder="+255..." />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Message / Truck of Interest</label>
                  <textarea required rows={4} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} className="w-full bg-gray-50 border border-gray-300 p-3 rounded-sm focus:border-automax-red outline-none text-gray-900 placeholder-gray-500" placeholder="I am interested in..."></textarea>
                </div>

                <button type="submit" disabled={isSending} className={`w-full bg-automax-red hover:bg-red-700 text-white font-bold text-lg uppercase py-4 rounded-sm transition-all flex items-center justify-center gap-2 ${isSending ? 'opacity-70 cursor-not-allowed' : ''}`}>
                  {isSending ? 'Sending...' : <>Send Message <Send size={20} /></>}
                </button>
              </div>
            )}
          </form>


        </div>
      </div>
    </div>
  );
};

export default Contact;
